import { 
    applicationsCollection, 
    serverTimestamp, 
    doc, 
    addDoc, 
    updateDoc, 
    deleteDoc, 
    onSnapshot 
} from './firebase.js';
import { showToast } from './uiManager.js';

// Helper type for Application
export type Application = {
    id?: string;
    companyName: string;
    jobTitle: string;
    dateApplied: string;
    status: string;
    jobUrl?: string;
    notes?: string;
    createdAt?: any;
    lastUpdatedAt?: any;
};

export class ApplicationManager {
    private allApplications: Application[];
    constructor() {
        this.allApplications = [];
    }

    subscribeToApplications(callback: (apps: any[]) => void) {
        const unsubscribe = onSnapshot(applicationsCollection, (snapshot) => {
            this.allApplications = snapshot.docs.map((doc: any) => ({
                ...doc.data(),
                id: doc.id
            }));
            callback(this.allApplications);
        }, (error: any) => {
            console.error("Error fetching applications: ", error);
            showToast("Error fetching applications. Check console.", "error");
            callback([]);
        });
        return unsubscribe;
    }

    async addApplication(applicationData: any) {
        try {
            applicationData.createdAt = serverTimestamp();
            await addDoc(applicationsCollection, applicationData);
            showToast('Application added successfully!');
            return true;
        } catch (error) {
            console.error("Error adding application: ", error);
            showToast('Error saving application. Check console.', 'error');
            return false;
        }
    }

    async updateApplication(appId: string, applicationData: any) {
        try {
            applicationData.lastUpdatedAt = serverTimestamp();
            await updateDoc(doc(applicationsCollection, appId), applicationData);
            showToast('Application updated successfully!');
            return true;
        } catch (error) {
            console.error("Error updating application: ", error);
            showToast('Error updating application. Check console.', 'error');
            return false;
        }
    }

    async deleteApplication(appId: string) {
        try {
            await deleteDoc(doc(applicationsCollection, appId));
            showToast('Application deleted successfully!');
            return true;
        } catch (error) {
            console.error("Error deleting application: ", error);
            showToast('Error deleting application. Check console.', 'error');
            return false;
        }
    }

    getApplicationById(appId: string) {
        return this.allApplications.find((app: any) => app.id === appId);
    }

    getAllApplications() {
        return this.allApplications;
    }

    filterApplications(searchTerm: string, statusFilter: string, sortDirection: string) {
        let filtered = [...this.allApplications];

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter((app: any) => 
                (app.companyName && app.companyName.toLowerCase().includes(term)) ||
                (app.jobTitle && app.jobTitle.toLowerCase().includes(term))
            );
        }

        if (statusFilter) {
            filtered = filtered.filter((app: any) => app.status === statusFilter);
        }

        // Sort by date
        filtered.sort((a: any, b: any) => {
            const dateA = new Date(a.dateApplied || 0).getTime();
            const dateB = new Date(b.dateApplied || 0).getTime();
            return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
        });

        return filtered;
    }

    getStats() {
        const stats = {
            total: this.allApplications.length,
            interviewing: 0,
            offers: 0,
            awaitingResponse: 0
        };

        this.allApplications.forEach((app: any) => {
            if (app.status === 'Interviewing') stats.interviewing++;
            else if (app.status === 'Offer') stats.offers++;
            else if (['Applied', 'FollowUp', 'Screening', 'Thinking'].includes(app.status)) {
                stats.awaitingResponse++;
            }
        });

        return stats;
    }

    exportToJSON() {
        const dataToExport = this.allApplications.map((app: any) => {
            const appExport = {...app};
            // Convert Firestore timestamps to ISO strings
            if (appExport.createdAt && appExport.createdAt.toDate) {
                appExport.createdAt = appExport.createdAt.toDate().toISOString();
            }
            if (appExport.lastUpdatedAt && appExport.lastUpdatedAt.toDate) {
                appExport.lastUpdatedAt = appExport.lastUpdatedAt.toDate().toISOString();
            }
            return appExport;
        });

        const jsonString = JSON.stringify(dataToExport, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        const date = new Date();
        const dateString = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
        const filename = `job_applications_export_${dateString}.json`;
        
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        showToast('Data exported successfully!', 'success');
    }
}